// Business Settings' rules, shared by the provider, the panels and the phones'
// Settings screens: what a save sends (`src/data/useSettings.jsx`), what a
// typed value commits as, the options each radio offers, and the words
// (`src/components/settings/*`). The argument for each rule is beside the code
// that uses it. The studio record's own shape and defaults are `./studio.js`;
// the sending identity is `./sendingIdentity.js`.
//
// Nothing here is the Membership tab: the teacher's own subscription is
// web-only (`MembershipPanel.jsx`, `billingPlans.js`), and the Mobile app tab
// links a phone *to* this studio, which a phone does not need to do to itself.

import {
  BALANCE_DATE_MODES, NEGATIVE_INVOICE_DISPLAYS, DEFAULT_ACCENT_COLOR, studioFromCore, studioToCore,
} from './studio.js';
import { coreSync } from './core.js';
import { documentFromRows, documentToRows } from './documents.js';
import { DEFAULT_SENDER_NAME } from './sendingIdentity.js';

// ─── The rules are `core/src/settings.rs`'s ────────────────────────────────
//
// What a save sends (the merge and the fields a merging set names — #337,
// #344, #345), what each typed box commits as, the Email Timeframe's and the
// balance date's sentences and the sending notice are adapters over the core,
// so a phone's Settings builds the same documents and says the same things.
// The tables below stay as literals for the panels that read them at render,
// pinned to the core's by test/core/settings.wasm.test.js.

export const STUDIO_GROUPS = Object.freeze(['invoicing', 'messaging', 'makeup', 'scheduling', 'nameFormats']);

const isGroup = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// A patch as the core's rows: a top-level field by its name, a group's leaf
// dotted (`invoicing.showLocation`), a group given as an empty map as a `map`
// row. `undefined` is read as null.
function patchRows(patch = {}) {
  const rows = [];
  const cell = (field, v) => documentToRows({ [field]: v === undefined ? null : v });
  for (const [key, value] of Object.entries(patch)) {
    if (isGroup(value)) {
      rows.push({ field: key, kind: 'map', text: null, flag: false, integer: 0, double: 0, items: [] });
      for (const [leaf, v] of Object.entries(value)) rows.push(...cell(`${key}.${leaf}`, v));
    } else {
      rows.push(...cell(key, value));
    }
  }
  return rows;
}

// One save: the record the patch makes of `base`, and the fields a merging
// set sends — the whole record on a create (`stored` false), otherwise only
// what the patch named, a group by the leaves it named — with `updatedAt` as
// the sentinel handed in (left off when none is).
export function studioSave(base, patch = {}, stored = true, serverTimestamp = undefined) {
  const core = coreSync();
  const rows = patchRows(patch);
  const record = studioToCore(base);
  const written = core.studioSaveDocument(record, rows, Boolean(stored));
  const fields = documentFromRows(
    serverTimestamp === undefined ? written.filter((r) => r.field !== 'updatedAt') : written,
    serverTimestamp,
  );
  return { next: studioFromCore(core.studioAfterSave(record, rows)), fields };
}

// ─── What a typed value commits as ─────────────────────────────────────────

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// Invoice Accent Color's hex box: a six-digit hex, lower-cased, or the colour
// already stored.
export function accentColorDraft(draft, current) {
  return coreSync().accentColorDraft(String(draft || ''), String(current ?? ''));
}
export const isHexColor = (v) => coreSync().isHexColor(String(v ?? ''));

export const balanceDayDraft = (draft) => coreSync().balanceDayDraft(num(draft));
export const overdueDaysDraft = (draft) => coreSync().overdueDaysDraft(num(draft));
export const holdDaysDraft = (draft) => coreSync().holdDaysDraft(num(draft));
export const makeupCapDraft = (draft) => coreSync().makeupCapDraft(num(draft)) ?? null;
export const makeupExpiryDraft = (draft) => coreSync().makeupExpiryDraft(num(draft)) ?? null;

// The Policies tab's three number boxes (#766), by leaf — `minAdvanceHours`,
// `maxAdvanceDays`, `deadlineHours`: rounded and clamped, an empty box MMS's
// default (`policy_number_draft` in core/src/settings.rs).
export const policyNumberDraft = (leaf, draft) => coreSync().policyNumberDraft(String(leaf), num(draft)) ?? null;

// What ticking "Limit number of make-up credits" and "Expire unused make-up
// credits" start at.
export const MAKEUP_CAP_WHEN_TURNED_ON = 3;
export const MAKEUP_EXPIRY_DAYS_WHEN_TURNED_ON = 90;

// ─── The options each radio and select offers ─────────────────────────────

export const BALANCE_DATE_OPTIONS = Object.freeze([
  { id: BALANCE_DATE_MODES.today, label: 'Today' },
  { id: BALANCE_DATE_MODES.endOfMonth, label: 'End of month' },
  { id: BALANCE_DATE_MODES.dayOfMonth, label: 'Day of month' },
  { id: BALANCE_DATE_MODES.specificDate, label: 'Specific Date' },
]);

export const NEGATIVE_DISPLAY_OPTIONS = Object.freeze([
  { id: NEGATIVE_INVOICE_DISPLAYS.zeroDue, label: 'Show as "$0.00 due"' },
  { id: NEGATIVE_INVOICE_DISPLAYS.balanceRemaining, label: 'Show as "Balance Remaining"' },
]);

// Negative Invoices' radios as the studio reads them (#528): the first writes
// the studio's own zero — `zero` is its `formatMoney(0)` — where the list
// above is a US studio's. MMS's label for a Germany - German studio reads
// `Show as "0,00 € due"`, the rest of the sentence English. The wording is the
// core's (`negative_display_options`).
export function negativeDisplayOptions(zero) {
  return coreSync().negativeDisplayOptions(String(zero ?? ''));
}

export const NAME_ROLE_LABELS = Object.freeze({
  teacher: 'Teacher Name Format',
  student: 'Student Name Format',
  parent: 'Parent Name Format',
});

// The Email Timeframe's hours: every start hour, and the hour each window
// ends — one later, midnight after 11 PM. The words around the two times are
// `sendWindowLabel`; the times themselves are the platform's locale format.
export const SEND_WINDOW_START_HOURS = Object.freeze(Array.from({ length: 24 }, (_, h) => h));
export const sendWindowEndHour = (start) => coreSync().sendWindowEndHour(Number(start));
export const sendWindowLabel = (startText, endText) => coreSync().sendWindowLabel(String(startText), String(endText));

// The sentence under the Email Timeframe, which names the zone the hours are
// in, or says there is none and what that means.
export const sendWindowHelp = (timeZone) => coreSync().sendWindowHelp(timeZone ? String(timeZone) : null);

export const balanceDateNote = (date, datePattern) => coreSync().balanceDateNote(String(date), String(datePattern ?? ''));
export const balanceSpecificDateHint = (date) => coreSync().balanceSpecificDateHint(date ? String(date) : null) ?? null;

// What the profile card calls the studio.
export const studioCardName = (name) => coreSync().studioCardName(name ? String(name) : null);

// ─── The sending-identity notice under Email Settings ──────────────────────

// Whether the notice shows, which of its two titles, and the envelope it
// previews. `brandName` is the product's, from `BRAND` — the only place it is
// spelled.
export function sendingNotice({ studioName, studioEmail, brandName } = {}) {
  const n = coreSync().sendingNotice(
    studioName == null ? null : String(studioName), studioEmail == null ? null : String(studioEmail),
    // The envelope's "via" is the sender's default name, which is the
    // product's — `sendingIdentity`'s, whatever `brandName` the caller says.
    DEFAULT_SENDER_NAME,
  );
  if (!n.rewritten) return { rewritten: false };
  return {
    rewritten: true,
    freeProvider: n.freeProvider,
    provider: n.provider ?? null,
    title: n.title,
    fromName: n.fromName,
    replyTo: n.replyTo,
    brandName: brandName || null,
  };
}

// The notice's paragraph, for a free provider or for any other domain.
export const sendingNoticeText = ({ freeProvider, provider, brandName } = {}) => (
  coreSync().sendingNoticeText(Boolean(freeProvider), String(provider ?? ''), String(brandName ?? ''))
);

// The paragraph around its one emphasised word: "mail sent *as* a gmail.com
// address". The core holds words and no markup, so the web finds the word here
// to set it in italics, and the phones show the sentence plain. A sentence
// without " sent as " comes back whole, as `before`.
const SENT_AS = ' sent as ';
export function sendingNoticeParts(text) {
  const whole = String(text ?? '');
  const at = whole.indexOf(SENT_AS);
  if (at < 0) return { before: whole, emphasis: null, after: '' };
  return {
    before: whole.slice(0, at + ' sent '.length),
    emphasis: 'as',
    after: whole.slice(at + SENT_AS.length - 1),
  };
}

// The line under Email Settings' heading.
export const emailSettingsBlurb = (brandName) => coreSync().emailSettingsBlurb(String(brandName ?? ''));

// ─── The Policies tab (#766) ───────────────────────────────────────────────
//
// Its words and its radios are the core's alone (`POLICY_WORDING` and the
// option tables in core/src/settings.rs, where what MMS does and where we
// differ is written), read through these rather than copied into a literal —
// the phones read the same two calls.

/** One of the Policies tab's words by code, or the code itself. */
export const policyWord = (code) => coreSync().settingsMessage(String(code)) ?? String(code);

/**
 * One of the Policies tab's radio lists: `beforeDeadlineAttendance`,
 * `afterDeadlineAttendance` or `cancelSlot`, each `[{ id, label }]`.
 */
export const policyOptions = (kind) => [...coreSync().settingsOptions(String(kind))];

// ─── The words ─────────────────────────────────────────────────────────────

// Every fixed label, heading and help line on the settings panels this app
// shares with the phones, by code.
export const SETTINGS_WORDING = Object.freeze({
  pageTitle: 'Business Settings',
  tabGeneral: 'General',
  tabInvoicing: 'Accounts & Invoices',
  tabMessaging: 'Email & SMS',
  editProfile: 'Edit Business Profile',
  profileTitle: 'Edit Studio Profile',
  profileDone: 'Done',
  regionFact: 'Region',
  timeZoneFact: 'Time Zone',
  phoneFact: 'Phone',
  addressFact: 'Address',
  thisDevice: '(this device)',
  studioName: 'Studio Name',
  phoneNumber: 'Phone Number',
  studioAddress: 'Studio Address',
  optional: 'Optional',
  region: 'Region',
  regionHint: 'Your selection controls currency, mileage log format, and date/time format',
  timeZone: 'Time Zone',
  eventScheduling: 'Event Scheduling',
  conflictsLabel: 'Scheduling Conflicts',
  conflictsCheck: 'Check for scheduling conflicts when adding/editing calendar events',
  multiTimeZoneLabel: 'Enable Multi-Time Zone Selection',
  multiTimeZoneCheck: 'Allow students to select a different time zone than yours',
  multiTimeZoneHelp: 'Use if you have remote students. Their lesson time is shown to them in their own zone; the lesson itself does not move.',
  holdLabel: 'Weekly time slot hold duration',
  holdUnit: 'days',
  holdHelp: 'Specifies how many days a student’s weekly time slot will be reserved after their original event has passed.',
  namingFormat: 'Naming Format',
  familyAccount: 'Family Account',
  balanceDateLabel: 'Default Balance Date',
  balanceDayLabel: 'Day',
  balanceDayHint: '1–28, so the date exists in every month',
  balanceDateField: 'Date',
  notifications: 'Notifications & Reminders',
  invoiceSmsCheck: 'Send SMS invoice notifications and invoice reminders',
  invoiceSmsHelp: 'Off, families are emailed about invoices and not texted.',
  overdueCheck: 'Send an overdue invoice reminder',
  overdueHelp: 'Families are sent one reminder about each unpaid invoice, once it is the chosen number of days past due, within your Email Timeframe.',
  overdueDaysLabel: 'Enter the number of days after invoice due date to send overdue reminder',
  overdueDaysUnit: 'days',
  emailTimeframe: 'Email Timeframe',
  invoiceFormatting: 'Invoice Formatting',
  invoiceName: 'Invoice Name',
  invoiceNameHint: 'Optional — appears in place of the studio name on the invoice',
  invoiceNamePlaceholder: 'Your studio name',
  additionalSettings: 'Additional Settings',
  negativeInvoices: 'Negative Invoices',
  showLocation: 'Show location on invoice',
  groupCharges: 'Group charges together by student for families with multiple students',
  footerText: 'Invoice Footer Text',
  accentColor: 'Invoice Accent Color',
  accentColorHint: `A hex colour — ${DEFAULT_ACCENT_COLOR} by default`,
  emailSettings: 'Email Settings',
  studioEmail: 'Studio Email Address',
  studioEmailPlaceholder: 'you@example.com',
  useStudioName: 'Use studio name as default sender',
  useStudioNameHelp: 'Your studio name appears as the sender; the address stays ours.',
  identityOnReminders: 'Use studio name & email on reminders',
  identityOnRemindersHelp: 'Lesson and invoice reminders go out from your address, with replies to you.',
  identityOnLessonNotes: 'Use studio name & email on lesson notes',
  makeupPolicy: 'Make-Up Credit Policy',
  makeupPolicyBlurb: 'These settings allow you to fine-tune your make-up policy.',
  makeupCapCheck: 'Limit number of make-up credits',
  makeupCapHelp: 'How many unused credits a student may hold at once.',
  makeupCapUnit: 'credits per student',
  makeupExpiryCheck: 'Expire unused make-up credits',
  makeupExpiryHelp: 'Unused credits stop counting once they reach this age.',
  makeupExpiryUnit: 'days after the credit was issued',
  envelopeFrom: 'From:',
  envelopeReplyTo: 'Reply-To:',
  envelopeUnknown: '<this deployment\u2019s verified sending address>',
});
